import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getApiUrl } from "../../../../api";
import { useAuthStore } from "../../../../store/authStore";
import MenuRegister from "./MenuRegister";
import MenuDetail from "./MenuDetail";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface MenuItem {
    id: string;
    no: number;
    name: string;
    visible: boolean;
    selected: boolean;
    category_type: string;
}

interface ConfirmModal {
    open: boolean;
    title: string;
    description: string;
    onConfirm: (() => Promise<void>) | null;
}

interface SortableRowProps {
    item: MenuItem;
    idx: number;
    totalCount: number;
    onRowClick: (item: MenuItem) => void;
    onViewDetail: (id: string) => void;
    onDelete: (id: string) => void;
    onToggleVisible: (id: string) => void;
    onSelectItem: (id: string) => void;
}

function SortableRow({ item, onRowClick, onViewDetail, onDelete, onToggleVisible, onSelectItem }: SortableRowProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragging ? "#fdf8f0" : undefined,
        zIndex: isDragging ? 9999 : undefined,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
            onClick={() => onRowClick(item)}
        >
            <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={item.selected} onChange={() => onSelectItem(item.id)} className="w-4 h-4 cursor-pointer accent-[#887267]" />
            </td>
            <td className="px-5 py-4 text-[13px] text-[#5a3d32]">{item.no}</td>
            <td className="px-5 py-4">
                <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-[#2a1f1a]">{item.name}</span>
                    <button
                        onClick={(e) => { e.stopPropagation(); onViewDetail(item.id); }}
                        className="px-3 py-1 bg-white border border-[#887267] rounded text-[#887267] text-[11px] font-medium hover:bg-[#f5f2f0] transition-colors whitespace-nowrap"
                    >상세 보기</button>
                </div>
            </td>
            <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={() => onToggleVisible(item.id)}
                    className={`relative w-16 h-7 rounded-3xl transition-colors ${item.visible ? "bg-[#887267]" : "bg-[#d0c4be]"}`}
                >
                    <div className={`absolute top-0.5 w-10 h-6 bg-white rounded-3xl transition-all ${item.visible ? "right-0.5" : "left-0.5"}`} />
                    <span className={`absolute top-1/2 -translate-y-1/2 text-[11px] font-normal ${item.visible ? "right-2 text-white" : "left-2 text-[#888]"}`}>
                        {item.visible ? "보임" : "숨김"}
                    </span>
                </button>
            </td>
            <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col gap-0.5 cursor-grab active:cursor-grabbing p-2 items-center" {...attributes} {...listeners}>
                    <div className="w-4 h-0.5 bg-[#b8a39b]"></div>
                    <div className="w-4 h-0.5 bg-[#b8a39b]"></div>
                    <div className="w-4 h-0.5 bg-[#b8a39b]"></div>
                </div>
            </td>
            <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={() => onDelete(item.id)}
                    className="px-3 py-1 text-[12px] border border-red-200 text-red-400 rounded hover:bg-red-50 transition-colors whitespace-nowrap"
                >삭제</button>
            </td>
        </tr>
    );
}

export default function MenuList() {
    const { role } = useAuthStore();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const kioskCode = searchParams.get("kiosk_code") || localStorage.getItem("kiosk_code") || "";
    const parentCategoryCode = searchParams.get("parent_category");

    const [menuData, setMenuData] = useState<MenuItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [breadcrumb, setBreadcrumb] = useState<{ category_code: string; name: string }[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedCategoryCode, setSelectedCategoryCode] = useState<string | null>(null);
    const [categoryList, setCategoryList] = useState<{ category_code: string; name: string; parent_category?: string }[]>([]);
    const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ open: false, title: "", description: "", onConfirm: null });

    useEffect(() => {
        const handlePopState = () => {
            setShowCreateModal(false);
            setShowDetailModal(false);
        };
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const getCmsToken = () => localStorage.getItem("cms_access_token");

    useEffect(() => {
        const fetchCategories = async () => {
            if (!kioskCode) return;
            try {
                const res = await fetch(getApiUrl(`/api/solutions/by-kiosk/${kioskCode}/categories`), {
                    headers: { Authorization: `Bearer ${getCmsToken()}` }
                });
                if (res.ok) { const data = await res.json(); setCategoryList(data); }
            } catch (e) { console.error(e); }
        };
        fetchCategories();
    }, [kioskCode]);

    const fetchData = async () => {
        if (!kioskCode) return;
        setLoading(true);
        try {
            const url = parentCategoryCode
                ? `/api/categories/children?category_code=${parentCategoryCode}`
                : `/api/categories/root?kiosk_code=${kioskCode}`;
            const res = await fetch(getApiUrl(url), { headers: { Authorization: `Bearer ${getCmsToken()}` } });
            if (res.ok) {
                const data = await res.json();
                if (data.parent) {
                    const chain: { category_code: string; name: string }[] = [];
                    let current: { category_code: string; name: string; parent_category?: string } | undefined = data.parent;
                    while (current) {
                        chain.unshift(current);
                        const found = categoryList.find(c => c.category_code === current?.parent_category);
                        current = found;
                    }
                    setBreadcrumb(chain);
                } else if (parentCategoryCode) {
                    // 하위메뉴 없어도 parentCategoryCode 있으면 브레드크럼 세팅
                    const found = categoryList.find(c => c.category_code === parentCategoryCode);
                    if (found) setBreadcrumb([found]);
                } else {
                    setBreadcrumb([]);
                }
                const categories = data.children || data;
                setMenuData(categories.map((item: { category_code: string; name: string; is_visible: number; category_type: string }, index: number) => ({
                    id: item.category_code,
                    no: index + 1,
                    name: item.name,
                    visible: item.is_visible === 1,
                    selected: false,
                    category_type: item.category_type,
                })));
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, [kioskCode, parentCategoryCode]);

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = menuData.findIndex(i => i.id === active.id);
        const newIndex = menuData.findIndex(i => i.id === over.id);
        const newData = arrayMove(menuData, oldIndex, newIndex).map((item, i) => ({ ...item, no: i + 1 }));
        setMenuData(newData);
        try {
            const res = await fetch(getApiUrl("/api/solutions/categories/sort"), {
                method: "PUT",
                headers: { Authorization: `Bearer ${getCmsToken()}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                    kiosk_code: kioskCode,
                    parent_category_code: parentCategoryCode || "CA00000",
                    items: newData.map((item, i) => ({ category_code: item.id, sort_order: i + 1 }))
                })
            });
            if (!res.ok) fetchData();
        } catch { fetchData(); }
    };

    const handleSelectAll = () => {
        const next = !selectAll;
        setSelectAll(next);
        setMenuData(menuData.map(item => ({ ...item, selected: next })));
    };

    const handleSelectItem = (id: string) =>
        setMenuData(menuData.map(item => item.id === id ? { ...item, selected: !item.selected } : item));

    const handleToggleVisible = async (id: string) => {
        const target = menuData.find(item => item.id === id);
        if (!target) return;
        const newVisible = !target.visible;
        setMenuData(prev => prev.map(item => item.id === id ? { ...item, visible: newVisible } : item));
        try {
            const res = await fetch(getApiUrl(`/api/categories/${id}/visible`), {
                method: "PATCH",
                headers: { Authorization: `Bearer ${getCmsToken()}`, "Content-Type": "application/json" },
                body: JSON.stringify({ is_visible: newVisible ? 1 : 0 })
            });
            if (!res.ok) setMenuData(prev => prev.map(item => item.id === id ? { ...item, visible: !newVisible } : item));
        } catch {
            setMenuData(prev => prev.map(item => item.id === id ? { ...item, visible: !newVisible } : item));
        }
    };

    const handleRowClick = (item: MenuItem) => {
        if (item.category_type !== "menu" && item.category_type !== "content") return;
        if (item.category_type === "content") navigate(`/admin/kiosk/content?kiosk_code=${kioskCode}&category_code=${item.id}`);
        else navigate(`/admin/kiosk/menu?kiosk_code=${kioskCode}&parent_category=${item.id}`);
    };

    const handleDeleteSelected = () => {
        const selectedItems = menuData.filter(item => item.selected);
        if (selectedItems.length === 0) return;
        setConfirmModal({
            open: true,
            title: `선택한 ${selectedItems.length}개의 메뉴를 삭제하시겠습니까?`,
            description: "삭제된 데이터는 복구할 수 없습니다.",
            onConfirm: async () => {
                try {
                    await Promise.all(selectedItems.map(item =>
                        fetch(getApiUrl(`/api/categories/${item.id}`), { method: "DELETE", headers: { Authorization: `Bearer ${getCmsToken()}` } })
                    ));
                    setSelectAll(false);
                    fetchData();
                } catch (e) { console.error(e); }
                finally { setConfirmModal(prev => ({ ...prev, open: false })); }
            }
        });
    };

    const handleDelete = (id: string) => {
        setConfirmModal({
            open: true,
            title: "이 메뉴를 삭제하시겠습니까?",
            description: "삭제된 데이터는 복구할 수 없습니다.",
            onConfirm: async () => {
                try {
                    await fetch(getApiUrl(`/api/categories/${id}`), { method: "DELETE", headers: { Authorization: `Bearer ${getCmsToken()}` } });
                    fetchData();
                } catch (e) { console.error(e); }
                finally { setConfirmModal(prev => ({ ...prev, open: false })); }
            }
        });
    };

    const handleViewDetail = (categoryCode: string) => {
        setSelectedCategoryCode(categoryCode);
        setShowDetailModal(true);
    };

    const hasSelected = menuData.some(item => item.selected);
    const selectedCount = menuData.filter(i => i.selected).length;

    return (
        <div className="bg-[#f5f2f0] min-h-screen p-6">
            <div className="max-w-[1200px] mx-auto">

                {/* 헤더 */}
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">키오스크 메뉴 관리</h1>
                        {breadcrumb.length > 0 && (
                            <div className="flex items-center gap-1.5 text-[13px] text-gray-400 flex-wrap mt-1">
                                <span>키오스크 메뉴 관리</span>
                                {breadcrumb.map((item) => (
                                    <span key={item.category_code} className="flex items-center gap-1.5">
                                        <span>›</span>
                                        <span className="text-[#887267] font-semibold">{item.name}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {role !== 2 && hasSelected && (
                            <button
                                onClick={handleDeleteSelected}
                                className="px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 transition-colors text-[13px] font-semibold"
                            >
                                선택 삭제 ({selectedCount})
                            </button>
                        )}
                        {role !== 2 && (
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] transition-colors text-[13px] font-semibold flex items-center gap-1.5"
                            >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="white"><path d="M14 8H8V14H6V8H0V6H6V0H8V6H14V8Z" /></svg>
                                신규 메뉴 등록
                            </button>
                        )}
                    </div>
                </div>

                {/* 테이블 */}
                <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                                <th className="px-5 py-3 w-[48px]">
                                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="w-4 h-4 cursor-pointer accent-[#887267]" />
                                </th>
                                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">NO</th>
                                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium">메뉴명</th>
                                <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">노출</th>
                                <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">순서</th>
                                <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-[14px]">불러오는 중...</td></tr>
                            ) : menuData.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-12 text-gray-300 text-[13px]">등록된 메뉴가 없습니다.</td></tr>
                            ) : (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={menuData.map(i => i.id)} strategy={verticalListSortingStrategy}>
                                        {menuData.map((item, idx) => (
                                            <SortableRow
                                                key={item.id}
                                                item={item}
                                                idx={idx}
                                                totalCount={menuData.length}
                                                onRowClick={handleRowClick}
                                                onViewDetail={handleViewDetail}
                                                onDelete={handleDelete}
                                                onToggleVisible={handleToggleVisible}
                                                onSelectItem={handleSelectItem}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <MenuRegister isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); fetchData(); }} kioskCode={kioskCode} defaultParentCode={parentCategoryCode || ""} />
            <MenuDetail isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedCategoryCode(null); }} kioskCode={kioskCode} categoryCode={selectedCategoryCode} />

            {/* 삭제 확인 모달 */}
            {confirmModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
                        <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">{confirmModal.title}</h3>
                        {confirmModal.description && <p className="text-[13px] text-gray-400 mb-5">{confirmModal.description}</p>}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]"
                            >취소</button>
                            <button
                                onClick={() => confirmModal.onConfirm?.()}
                                className="flex-1 px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 text-[13px] font-semibold"
                            >삭제</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}